import { useEffect, useState } from 'react'
import {Download, QrCode} from 'lucide-react'
import toast from 'react-hot-toast'
import QRCode from 'qrcode'

interface QrCodesPersonalizadosProps {
  usuarioConsultora: string
}

const COR_FUNDO = '#FFFFFF'
const TAMANHO_EXIBICAO = 200
const TAMANHO_DOWNLOAD = 500

async function gerarQrDataUrl(
  texto: string,
  tamanho: number,
  _corQr: string,
): Promise<string> {
  return QRCode.toDataURL(texto, {
    width: tamanho,
    margin: 2,
    color: {
      dark: '#000000',
      light: COR_FUNDO,
    },
    errorCorrectionLevel: 'H',
  })
}

function QrCard({
  titulo,
  descricao,
  url,
  nomeArquivo,
  icone,
  corHeader,
  corQr,
  rotuloBotao,
}: {
  titulo: string
  descricao: string
  url: string
  nomeArquivo: string
  icone: React.ReactNode
  corHeader: string
  corQr: string
  rotuloBotao: string
}) {
  const [dataUrl, setDataUrl] = useState<string>('')
  const [gerando, setGerando] = useState(false)

  useEffect(() => {
    let ativo = true
    gerarQrDataUrl(url, TAMANHO_EXIBICAO, corQr)
      .then((data) => {
        if (ativo) setDataUrl(data)
      })
      .catch((erro) => {
        console.error('Erro ao gerar QR Code:', erro)
      })
    return () => {
      ativo = false
    }
  }, [url, corQr])

  async function baixar() {
    setGerando(true)
    try {
      const hd = await gerarQrDataUrl(url, TAMANHO_DOWNLOAD, corQr)
      const link = document.createElement('a')
      link.href = hd
      link.download = nomeArquivo
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('QR Code baixado!')
    } catch {
      toast.error('Erro ao gerar o QR Code')
    } finally {
      setGerando(false)
    }
  }

  return (
    <div className="flex flex-col items-center bg-white border-2 border-bege-medio rounded-2xl overflow-hidden">
      {/* Header */}
      <div
        className="w-full px-5 py-3 flex items-center gap-3"
        style={{ backgroundColor: corHeader }}
      >
        <span className="text-white flex-shrink-0">{icone}</span>
        <span className="text-white font-cormorant font-semibold text-lg leading-tight">
          {titulo}
        </span>
      </div>

      {/* QR Code */}
      <div
        className="flex items-center justify-center p-5"
        style={{ backgroundColor: COR_FUNDO }}
      >
        {dataUrl ? (
          <img
            src={dataUrl}
            alt={`QR Code — ${titulo}`}
            width={TAMANHO_EXIBICAO}
            height={TAMANHO_EXIBICAO}
            className="rounded-xl"
            style={{ imageRendering: 'pixelated' }}
          />
        ) : (
          <div
            className="rounded-xl flex items-center justify-center"
            style={{
              width: TAMANHO_EXIBICAO,
              height: TAMANHO_EXIBICAO,
              backgroundColor: COR_FUNDO,
            }}
          >
            <QrCode className="w-12 h-12 opacity-20" style={{ color: corQr }} />
          </div>
        )}
      </div>

      {/* Descrição e URL */}
      <div className="px-5 pb-2 w-full">
        <p className="text-xs text-texto-suave leading-relaxed mb-1">
          {descricao}
        </p>
        <p
          className="text-xs font-medium break-all"
          style={{ color: corQr }}
        >
          {url}
        </p>
      </div>

      {/* Botão download */}
      <div className="px-5 pb-5 w-full mt-3">
        <button
          onClick={baixar}
          disabled={!dataUrl || gerando}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm text-white hover:opacity-90 transition-all min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: corHeader }}
        >
          <Download className="w-4 h-4" />
          {gerando ? 'Gerando…' : rotuloBotao}
        </button>
      </div>
    </div>
  )
}

export default function QrCodesPersonalizados({
  usuarioConsultora,
}: QrCodesPersonalizadosProps) {
  const slug = String(usuarioConsultora || '').trim()
  const urlDiagnostico = `https://www.descubraseusoleos.com.br/?c=${encodeURIComponent(slug)}`
  const urlKids = `https://www.descubraseusoleos.com.br/kids?c=${encodeURIComponent(slug)}`
  const urlCapacitacao = `https://www.descubraseusoleos.com.br/capacitacao?indicado_por=${encodeURIComponent(slug)}`
  const nomeBase = slug.toLowerCase().replace(/\s+/g, '-') || 'consultora'

  // Cores: Adulto verde escuro (#2D6A4F), Kids marrom claro (#B08968 — mesma cor
  // do header do bloco "Diagnóstico Kids" em DivulgarLink), Capacitação terracota (#C17B3F)
  const COR_ADULTO = '#2D6A4F'
  const COR_KIDS = '#B08968'
  const COR_CAPACITACAO = '#C17B3F'

  return (
    <div className="space-y-6">
      {/* Cards dos três QR Codes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <QrCard
          titulo="Diagnóstico de Saúde e Emoções"
          descricao="Compartilhe este QR Code em cartazes, banners e materiais impressos. Qualquer pessoa que escanear será direcionada diretamente para o seu diagnóstico personalizado."
          url={urlDiagnostico}
          nomeArquivo={`qrcode-diagnostico-${nomeBase}.png`}
          corHeader={COR_ADULTO}
          corQr={COR_ADULTO}
          icone={<QrCode className="w-5 h-5" />}
          rotuloBotao="Baixar QR Code Adulto"
        />
        <QrCard
          titulo="Diagnóstico Kids"
          descricao="Compartilhe este QR Code para mães e responsáveis descobrirem quais óleos essenciais são indicados para seus filhos."
          url={urlKids}
          nomeArquivo={`qrcode-kids-${nomeBase}.png`}
          corHeader={COR_KIDS}
          corQr={COR_KIDS}
          icone={<QrCode className="w-5 h-5" />}
          rotuloBotao="Baixar QR Code Kids"
        />
        <QrCard
          titulo="Seleção e Capacitação"
          descricao="Compartilhe este QR Code para convidar pessoas para participar da seleção e capacitação gratuita na área de bem-estar, saúde física e emocional."
          url={urlCapacitacao}
          nomeArquivo={`qrcode-capacitacao-${nomeBase}.png`}
          corHeader={COR_CAPACITACAO}
          corQr={COR_CAPACITACAO}
          icone={<QrCode className="w-5 h-5" />}
          rotuloBotao="Baixar QR Code Capacitação"
        />
      </div>

      {/* Dica de uso */}
      <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-5 py-4 text-sm text-[#92400E] leading-relaxed">
        <p className="font-semibold mb-2">💡 Como usar seus QR Codes:</p>
        <ul className="space-y-1">
          <li>→ Imprima em cartazes e banners para consultórios e eventos</li>
          <li>→ Adicione em cartões de visita</li>
          <li>→ Insira em apresentações</li>
          <li>→ Compartilhe como imagem nas redes sociais</li>
          <li>→ Qualquer pessoa que escanear será direcionada ao seu link personalizado automaticamente</li>
        </ul>
      </div>
    </div>
  )
}
